//utility function that takes in the segments and parse through all the nodes
//  Example
/*
present.wheel_segments = updateWheelSegmentsNodes(
    present.wheel_segments,
    (node) => {
      if (node.type?.resolvedName === "ContainerWidget" && node.props) {
        node.props.radius = newCornerRounding;
      } else if (node?.props?.borderRadius) {
        node.props.borderRadius = newCornerRounding;
      }
    }
  );
  */

export const updateWheelSegmentsNodes = (segments, updater) => {
  return segments.map((segment) => {
    if (!segment.nodes) return segment;

    try {
      const parsedNodes = JSON.parse(segment.nodes);
      Object.entries(parsedNodes).forEach(([key, node]) => {
        updater(node); // Apply changes using the provided updater
      });
      segment.nodes = JSON.stringify(parsedNodes);
    } catch (error) {
      console.error("Error updating nodes:", error);
    }

    return segment;
  });
};

export function convertGlobalSettingsToApiFormat(globalSettings) {
  if (!globalSettings) return null;

  return {
    heading: {
      font_color: globalSettings?.headingProps?.fontColor || "black",
      font_family: globalSettings?.headingProps?.fontFamily || "Poppins",
      font_size: globalSettings?.headingProps?.fontSize || 20,
      reset_key: globalSettings?.headingProps?.resetKey || 0,
    },
    paragraph: {
      font_color: globalSettings?.paragraphProps?.fontColor || "black",
      font_family: globalSettings?.paragraphProps?.fontFamily || "Poppins",
      font_size: globalSettings?.paragraphProps?.fontSize || 14,
      reset_key: globalSettings?.paragraphProps?.resetKey || 0,
    },
    line_height: globalSettings?.lineHeight || 1.15,
    corner_radius: globalSettings?.cornerRounding || 8,
    border_thickness: globalSettings?.borderThickness || 8,
    margin: {
      top: globalSettings?.margin?.top || 20,
      bottom: globalSettings?.margin?.bottom || 20,
      left: globalSettings?.margin?.left || 20,
      right: globalSettings?.margin?.right || 20,
    },
  };
}

/**
 * Decide how the Wheel Builder should react when a wheel fails to load or the
 * request succeeds with no wheel payload (deleted / bad / inaccessible id).
 *
 * Pulled out as a pure function so the previously-silent failure path is
 * unit-testable. Before this existed, the builder's catch handler only reacted
 * to a response body `status_code === 400` and dereferenced
 * `error.response.data.message` unguarded — so a genuine HTTP 404 (DRF returns
 * `{ detail: "Not found." }`, no `message`, no body `status_code`) produced NO
 * toast and NO redirect, stranding the user on a blank, autosave-capable
 * builder shell (coverage cell 0.5).
 *
 * @param {object} [error] axios-style error; omit for the "200 with no wheel
 *   payload" case.
 * @returns {{ message: string, redirect: boolean }} message to toast and
 *   whether to redirect the user off the builder shell (always true — an
 *   unloadable wheel is never a valid builder state).
 */
export function resolveWheelLoadError(error) {
  const data = error?.response?.data;
  const message =
    data?.message ||
    data?.detail ||
    "This Wheel couldn't be opened. It may have been deleted or is no longer available.";
  return { message, redirect: true };
}

/**
 * Should the Builder evict the current user to the read-only viewer?
 *
 * batch2-0925-P1-fresh-dev-teams-coedit-multiuser: gates on the wheel's own
 * `is_editable` (server-authoritative, Teams-role-aware —
 * `workspaces.services.permissions.can(user, EDIT, wheel)`), NEVER on
 * comparing the wheel's literal DB owner id to the logged-in user's id. That
 * older ownership-only comparison evicted every workspace Editor/Admin from
 * a teammate's shared wheel, since they are never the `authorId` owner even
 * when their team role legitimately grants EDIT. `undefined` (not yet
 * fetched) must NOT redirect — only a fetched, explicit `false` does.
 * @param {boolean|undefined} isEditable
 * @returns {boolean}
 */
export function shouldRedirectOffBuilder(isEditable) {
  return isEditable === false;
}

export function revertGlobalSettingsFromApiFormat(apiGlobalSettings) {
  if (!apiGlobalSettings) return null;

  return {
    headingProps: {
      fontColor: apiGlobalSettings?.heading?.font_color || "black",
      fontFamily: apiGlobalSettings?.heading?.font_family || "Poppins",
      fontSize: apiGlobalSettings?.heading?.font_size || 20,
      resetKey: apiGlobalSettings?.heading?.reset_key || 0,
    },
    paragraphProps: {
      fontColor: apiGlobalSettings?.paragraph?.font_color || "black",
      fontFamily: apiGlobalSettings?.paragraph?.font_family || "Poppins",
      fontSize: apiGlobalSettings?.paragraph?.font_size || 14,
      resetKey: apiGlobalSettings?.paragraph?.reset_key || 0,
    },
    lineHeight: parseFloat(apiGlobalSettings?.line_height) || 1.15,
    cornerRounding: apiGlobalSettings?.corner_radius || 8,
    borderThickness: apiGlobalSettings?.border_thickness || 8,
    margin: {
      top: apiGlobalSettings?.margin?.top || 20,
      bottom: apiGlobalSettings?.margin?.bottom || 20,
      left: apiGlobalSettings?.margin?.left || 20,
      right: apiGlobalSettings?.margin?.right || 20,
    },
  };
}

const VALID_CENTER_MEDIA_TYPES = ["image", "gif", "video"];

/**
 * Sanitize a wheel's `wheel_center_media` before it reaches redux/the canvas.
 *
 * A DEAD/404 asset is still a perfectly valid `{ type, url, poster }` shape —
 * the url is simply unreachable, and the canvas layers (D3Wheels/createPie's
 * §12 broken-portrait listener, PortraitVideoOverlay's onVideoError) already
 * fall back to the brand mark when the browser fails to load it. This guard
 * is for the OTHER failure mode: a MALFORMED value (null, a string, an
 * unrecognized `type`, non-string fields) that could otherwise reach the
 * render tree ungated. It never throws and never strips a live url — a dead
 * url is passed through unchanged so the existing onerror fallback still
 * gets a chance to run.
 *
 * @param {*} centerMedia raw `wheel_center_media` from the API response
 * @returns {{type: 'image'|'gif'|'video', url: string, poster: string}}
 */
export function resolveSafeCenterMedia(centerMedia) {
  const FALLBACK = { type: "image", url: "", poster: "" };
  if (!centerMedia || typeof centerMedia !== "object") return FALLBACK;

  const type = VALID_CENTER_MEDIA_TYPES.includes(centerMedia.type)
    ? centerMedia.type
    : "image";

  return {
    type,
    url: typeof centerMedia.url === "string" ? centerMedia.url : "",
    poster: typeof centerMedia.poster === "string" ? centerMedia.poster : "",
  };
}

/**
 * Orchestrates a Builder wheel load: fetch the wheel record, then hydrate it
 * into the Builder — and classifies ANY failure into exactly one of two
 * buckets so a bug while HYDRATING an already-successfully-fetched wheel
 * (e.g. processing a wheel whose portrait/center media points at a dead/404
 * asset) can never be mistaken for "this wheel doesn't exist, evict the
 * user." Only a failure to FETCH the wheel's own record — a real network
 * error, HTTP 4xx/5xx, or a 200 with no payload — means the wheel itself is
 * unreachable and warrants redirecting off the Builder shell. The wheel
 * EXISTS in every other case; only some of its data/assets may be broken, so
 * the Builder must stay open.
 *
 * Pulled out as a plain, dependency-injected function (no React/redux) so
 * the two failure classes are independently unit-testable without mounting
 * the full Builder page.
 *
 * @param {object} o
 * @param {string|number} o.id wheel id
 * @param {(id: string|number) => Promise<any>} o.fetchWheelDetails
 * @param {(response: object) => (void|Promise<void>)} o.hydrate applies the
 *   fetched wheel payload to component/redux state; may throw
 * @param {(loadError: {message: string, redirect: boolean}) => void} o.onEvicted
 *   called when the wheel itself could not be fetched
 * @param {(error: unknown, response: object) => void} o.onHydrationFailed
 *   called when `hydrate` throws for an already-fetched wheel; the Builder
 *   stays mounted
 */
export async function loadWheelForBuilder({
  id,
  fetchWheelDetails,
  hydrate,
  onEvicted,
  onHydrationFailed,
}) {
  let response;
  try {
    const wheelDetails = await fetchWheelDetails(id);
    response = wheelDetails?.data?.data;
  } catch (error) {
    onEvicted(resolveWheelLoadError(error));
    return;
  }

  if (!response) {
    onEvicted(resolveWheelLoadError());
    return;
  }

  try {
    await hydrate(response);
  } catch (error) {
    onHydrationFailed(error, response);
  }
}
